import { yupResolver } from '@hookform/resolvers/yup';
import {
  OtherExpensesModel,
  useUpdateOtherExpense,
} from 'api-hooks/other-expense';
import formSetErrors from 'common/helpers/form-setError';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import Button from 'components/elements/buttons/button';
import useApprovalAction, { isDataDraft } from 'hooks/use-approval-action';
import useDismiss from 'hooks/use-dismiss';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import { isDataActive } from 'hooks/use-void-action';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  OtherExpenseUpdateFormSchema,
  OtherExpenseUpdateFormType,
} from './components/form-type';
import OtherExpenseCard from './components/other-expense-card';

interface OtherExpenseViewProps {
  otherExpense: OtherExpensesModel;
  onClose: () => void;
}

export default function OtherExpenseView(props: OtherExpenseViewProps) {
  const { otherExpense, onClose } = props;
  const { t } = useTranslation();
  const isActive = isDataActive(otherExpense.status);
  const { mutateAsync } = useUpdateOtherExpense();
  const { onRequestApproval, requestApprovalButton } = useApprovalAction(
    'other-expenses',
    otherExpense,
  );

  const onDismiss = useDismiss('other-expenses');
  const defaultValues = React.useMemo<OtherExpenseUpdateFormType>(() => {
    return {
      accountableId: otherExpense.accountable?.id || null,
      accountType: otherExpense.accountType || null,
      amount: otherExpense.amount,
      description: otherExpense.description || '',
      transactionAt: otherExpense.transactionAt,
    };
  }, [
    otherExpense.accountType,
    otherExpense.accountable?.id,
    otherExpense.amount,
    otherExpense.description,
    otherExpense.transactionAt,
  ]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(OtherExpenseUpdateFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: OtherExpenseUpdateFormType) => {
      try {
        const result = await mutateAsync({
          otherExpenseRefId: otherExpense.refId,
          body: values,
        });
        notification.success({
          message: result.message,
        });
        if (isDataDraft(result.data.status)) {
          onRequestApproval(result.data.id);
        } else {
          onDismiss(otherExpense.id);
          onDismiss(result.data.id);
        }
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        e.message &&
          notification.error({
            message: e.message,
          });
      }
    },
    [
      methods.setError,
      mutateAsync,
      onDismiss,
      onRequestApproval,
      otherExpense.id,
      otherExpense.refId,
      props,
    ],
  );
  const { revertDraftButton } = useRevertDraftAction(
    'other-expenses',
    otherExpense,
  );
  return (
    <>
      <DrawerHeader
        onClose={onClose}
        title={t(`common:edit_extra`, {
          extra: t('common:other_expenses'),
        })}
      />

      <DrawerBody
        style={{
          marginTop: 0,
        }}
      >
        <Form
          methods={methods}
          onSubmit={onSubmit}
          keyName={`${otherExpense.id}-otherExpense`}
          noReset
        >
          <OtherExpenseCard type="view" />
          <Separator gap={16} />
          <FullContainer
            style={{ display: 'flex', justifyContent: 'end', gap: 8 }}
          >
            {isActive ? (
              <>
                <Button buttonType="tertiary" onClick={props.onClose}>
                  {t('common:cancel')}
                </Button>
                <Input type="submit" />
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                {revertDraftButton}
                {requestApprovalButton}
              </div>
            )}
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}
